import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { api, formatError } from "@/lib/api";

const emptyFrom = (fields) =>
  Object.fromEntries(fields.map((f) => [f.name, f.default ?? (f.type === "number" ? 0 : "")]));

const Field = ({ f, value, onChange, prefix }) => {
  const id = `${prefix}-${f.name.replace(/_/g, "-")}`;
  if (f.type === "select") {
    return (
      <Select value={value || undefined} onValueChange={onChange}>
        <SelectTrigger data-testid={`${id}-select`}><SelectValue placeholder="Seleziona..." /></SelectTrigger>
        <SelectContent>
          {f.options.map((o) => (
            <SelectItem key={o.value ?? o} value={o.value ?? o}>{o.label ?? o}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  }
  if (f.type === "textarea") {
    return <Textarea data-testid={`${id}-input`} value={value} onChange={(e) => onChange(e.target.value)} rows={3} />;
  }
  return (
    <Input
      data-testid={`${id}-input`}
      type={f.type === "number" ? "number" : f.type || "text"}
      step={f.step}
      value={value}
      onChange={(e) => onChange(f.type === "number" ? e.target.value : e.target.value)}
    />
  );
};

export const EntityFormDialog = ({ open, onOpenChange, endpoint, fields, item, title, prefix, onSaved }) => {
  const [form, setForm] = useState(emptyFrom(fields));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setForm(item ? { ...emptyFrom(fields), ...item } : emptyFrom(fields));
  }, [open, item]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async (e) => {
    e.preventDefault();
    const missing = fields.find((f) => f.required && !String(form[f.name] ?? "").trim());
    if (missing) return toast.error(`Il campo "${missing.label}" è obbligatorio`);
    const payload = Object.fromEntries(
      fields.map((f) => [f.name, f.type === "number" ? Number(form[f.name]) || 0 : form[f.name] ?? ""])
    );
    setSaving(true);
    try {
      const { data } = item?.id ? await api.put(`/${endpoint}/${item.id}`, payload) : await api.post(`/${endpoint}`, payload);
      toast.success(item?.id ? "Modifiche salvate" : "Elemento creato");
      onSaved?.(data);
      onOpenChange(false);
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl" data-testid={`${prefix}-dialog`}>
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">{title}</DialogTitle>
          <DialogDescription className="sr-only">{title}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {fields.map((f) => (
            <div key={f.name} className={f.full || f.type === "textarea" ? "sm:col-span-2" : ""}>
              <Label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-slate-500">
                {f.label}{f.required && <span className="text-orange-600"> *</span>}
              </Label>
              <Field f={f} prefix={prefix} value={form[f.name] ?? ""} onChange={(v) => setForm((s) => ({ ...s, [f.name]: v }))} />
            </div>
          ))}
          <DialogFooter className="sm:col-span-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} data-testid={`${prefix}-cancel-button`}>Annulla</Button>
            <Button type="submit" disabled={saving} className="bg-orange-600 hover:bg-orange-700" data-testid={`${prefix}-save-button`}>
              {saving ? "Salvataggio..." : "Salva"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
